import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { exportToExcel } from '@/utils/exportUtils';
import { formatINR, toInputDate } from '@/utils/format';
import { getData } from '@/services/FetchNodeServices.js';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { formatWorkCenter } from '@/hooks/useWorkCenters';

// Department (cost center) report: cost, hours, output and budget of each Production / Quality /
// Maintenance cost center, through the work centers assigned to it.

interface Metrics {
  labourCost: number;
  materialCost: number;
  overheadCost: number;
  totalCost: number;
  actualHours: number;
  plannedHours: number;
  availableHours: number;
  utilizationPct: number | null;
  goodQty: number;
  rejectedQty: number;
  rejectionPct: number | null;
  downtimeHours: number;
  entries: number;
}

interface WorkCenterRow extends Metrics {
  id: string | null;
  code: string | null;
  name: string;
  type: string | null;
  isActive: boolean;
}

interface CostCenterRow extends Metrics {
  id: string | null;
  code: string | null;
  name: string;
  type: string | null;
  managerName: string | null;
  budget: number;
  budgetVariance: number | null;
  budgetUsedPct: number | null;
  workCenters: WorkCenterRow[];
}

interface ReportData {
  from: string;
  to: string;
  days: number;
  overheadPct: number;
  costCenters: CostCenterRow[];
  totals: Metrics & { budget: number; budgetVariance: number | null };
}

const hrs = (n: number) => `${(Math.round(n * 10) / 10).toLocaleString('en-IN')} h`;
const pct = (n: number | null) => (n === null ? '—' : `${n}%`);
const displayDate = (d: string) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const typeLabel = (t: string | null) => (t ? t.charAt(0) + t.slice(1).toLowerCase() : '—');

function utilizationTone(p: number | null) {
  if (p === null) return 'text-[#94a3b8]';
  if (p > 100) return 'text-rose-600';
  if (p >= 70) return 'text-emerald-600';
  if (p >= 40) return 'text-amber-600';
  return 'text-[#64748b]';
}

function BudgetBar({ used }: { used: number | null }) {
  if (used === null) return <span className="text-[#94a3b8]">No budget</span>;
  const tone = used > 100 ? 'bg-rose-500' : used > 85 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="min-w-[90px]">
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, used)}%` }} />
      </div>
      <span className={`text-[10px] ${used > 100 ? 'text-rose-600 font-semibold' : 'text-[#64748b]'}`}>{used}% used</span>
    </div>
  );
}

export default function CostCenterReportPage() {
  const navigate = useNavigate();
  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);
  const activeWarehouseId = selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined;

  // Default period: the current month to date
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return toInputDate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [toDate, setToDate] = useState(() => toInputDate(new Date()));
  const [costCenterFilter, setCostCenterFilter] = useState('ALL');
  const [hideIdle, setHideIdle] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getData('api/v1/reports/cost-centers', {
        fromDate,
        toDate,
        ...(activeWarehouseId ? { warehouseId: activeWarehouseId } : {}),
      });
      if (!res?.success || !res?.data) {
        setData(null);
        setError(res?.message || 'Could not load the cost center report');
        return;
      }
      setData(res.data as ReportData);
    } catch {
      setData(null);
      setError('Could not load the cost center report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, activeWarehouseId]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const rowKey = (cc: CostCenterRow) => cc.id ?? 'UNASSIGNED';

  const visible = useMemo(() => {
    const list = (data?.costCenters ?? []).filter((cc) => costCenterFilter === 'ALL' || rowKey(cc) === costCenterFilter);
    return list.map((cc) => ({
      ...cc,
      workCenters: hideIdle ? cc.workCenters.filter((w) => w.totalCost > 0 || w.actualHours > 0 || w.plannedHours > 0 || w.downtimeHours > 0) : cc.workCenters,
    }));
  }, [data, costCenterFilter, hideIdle]);

  const totals = data?.totals;
  const unassigned = data?.costCenters.find((cc) => cc.id === null);
  const unassignedShare = totals && unassigned && totals.totalCost > 0 ? Math.round((unassigned.totalCost / totals.totalCost) * 100) : 0;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const handleExport = () => {
    if (!data) return;
    const rows: Record<string, unknown>[] = [];
    for (const cc of visible) {
      const base = {
        'Cost Center': [cc.code, cc.name].filter(Boolean).join(' · '),
        Type: typeLabel(cc.type),
      };
      rows.push({
        ...base,
        'Work Center': 'TOTAL',
        'Labour ₹': cc.labourCost,
        'Material ₹': cc.materialCost,
        'Overhead ₹': cc.overheadCost,
        'Total Cost ₹': cc.totalCost,
        'Budget ₹': cc.budget || '',
        'Budget Used %': cc.budgetUsedPct ?? '',
        'Actual Hours': cc.actualHours,
        'Planned Hours': cc.plannedHours,
        'Available Hours': cc.availableHours,
        'Utilization %': cc.utilizationPct ?? '',
        'Good Qty': cc.goodQty,
        'Rejected Qty': cc.rejectedQty,
        'Rejection %': cc.rejectionPct ?? '',
        'Downtime Hours': cc.downtimeHours,
      });
      for (const w of cc.workCenters) {
        rows.push({
          ...base,
          'Work Center': formatWorkCenter(w.code, w.name),
          'Labour ₹': w.labourCost,
          'Material ₹': w.materialCost,
          'Overhead ₹': w.overheadCost,
          'Total Cost ₹': w.totalCost,
          'Budget ₹': '',
          'Budget Used %': '',
          'Actual Hours': w.actualHours,
          'Planned Hours': w.plannedHours,
          'Available Hours': w.availableHours,
          'Utilization %': w.utilizationPct ?? '',
          'Good Qty': w.goodQty,
          'Rejected Qty': w.rejectedQty,
          'Rejection %': w.rejectionPct ?? '',
          'Downtime Hours': w.downtimeHours,
        });
      }
    }
    const columns = Object.keys(rows[0] ?? {});
    exportToExcel(rows, columns.map((c) => ({ header: c, key: c })), `cost_center_report_${data.from}_${data.to}`);
  };

  const cards = totals
    ? [
        { label: 'Total Cost', value: formatINR(totals.totalCost), sub: `Labour ${formatINR(totals.labourCost)} · Material ${formatINR(totals.materialCost)}`, icon: 'ri-money-rupee-circle-line', tone: 'bg-indigo-50 text-indigo-600' },
        {
          label: 'Budget (period)',
          value: totals.budget > 0 ? formatINR(totals.budget) : '—',
          sub: totals.budgetVariance === null ? 'No cost center budgets set' : totals.budgetVariance > 0 ? `Over by ${formatINR(totals.budgetVariance)}` : `Under by ${formatINR(-totals.budgetVariance)}`,
          icon: 'ri-wallet-3-line',
          tone: totals.budgetVariance !== null && totals.budgetVariance > 0 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600',
        },
        { label: 'Utilization', value: pct(totals.utilizationPct), sub: `${hrs(totals.actualHours)} run of ${hrs(totals.availableHours)} available`, icon: 'ri-time-line', tone: 'bg-amber-50 text-amber-600' },
        { label: 'Output', value: totals.goodQty.toLocaleString('en-IN'), sub: `${totals.rejectedQty.toLocaleString('en-IN')} rejected (${pct(totals.rejectionPct)}) · ${hrs(totals.downtimeHours)} downtime`, icon: 'ri-stack-line', tone: 'bg-sky-50 text-sky-600' },
      ]
    : [];

  const th = 'px-3 py-2.5 text-[11px] font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap';
  const td = 'px-3 py-2.5 whitespace-nowrap';

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-4 bg-[#f8fafc] min-h-screen">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/reports')}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-[#64748b] cursor-pointer"
              title="Back to reports"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-[#1e293b]">Cost Center Report</h2>
              <p className="text-xs text-[#64748b] mt-0.5">
                Department-wise cost, hours and output · {displayDate(fromDate)} – {displayDate(toDate)}
                {data ? ` · overhead ${data.overheadPct}% on material + labour` : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-2 text-xs">
            <span className="text-[#64748b] font-medium">From</span>
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => e.target.value && setFromDate(e.target.value)}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
            />
            <span className="text-[#64748b] font-medium">To</span>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => e.target.value && setToDate(e.target.value)}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
            />
            <select
              value={costCenterFilter}
              onChange={(e) => setCostCenterFilter(e.target.value)}
              className="h-8 px-2 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5] cursor-pointer"
            >
              <option value="ALL">All cost centers</option>
              {(data?.costCenters ?? []).map((cc) => (
                <option key={rowKey(cc)} value={rowKey(cc)}>
                  {[cc.code, cc.name].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
            <button
              onClick={() => void fetchReport()}
              disabled={loading}
              className="h-8 px-4 rounded-lg bg-[#4f46e5] text-white font-medium hover:bg-indigo-700 cursor-pointer flex items-center gap-1.5 disabled:opacity-60"
            >
              <i className={`ri-refresh-line text-xs ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={!data || visible.length === 0}
              className="h-8 px-3 rounded-lg border border-[#e2e8f0] bg-white text-[#475569] font-medium hover:bg-slate-50 cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
            >
              <i className="ri-file-excel-2-line text-xs" />
              Export
            </button>
          </div>
        </div>

        {error && <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">{error}</div>}

        {/* Summary */}
        {cards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {cards.map((c) => (
              <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${c.tone}`}>
                  <i className={`${c.icon} text-lg`} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wide">{c.label}</p>
                  <p className="text-lg font-bold text-[#1e293b]">{c.value}</p>
                  <p className="text-[11px] text-[#94a3b8] truncate" title={c.sub}>{c.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {unassigned && unassignedShare > 0 && (
          <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex flex-wrap items-center justify-between gap-2">
            <span>
              <i className="ri-information-line mr-1" />
              {unassignedShare}% of the cost ({formatINR(unassigned.totalCost)}) is on work centers without a cost center, or on shop floor time with no work center recorded.
            </span>
            <button onClick={() => navigate('/masters/work-centers')} className="font-semibold underline cursor-pointer">
              Assign cost centers to work centers
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#e2e8f0]">
            <p className="text-sm font-semibold text-[#1e293b]">Cost centers {visible.length > 0 && <span className="text-[#94a3b8] font-normal">({visible.length})</span>}</p>
            <label className="flex items-center gap-1.5 text-xs text-[#64748b] cursor-pointer">
              <input type="checkbox" checked={hideIdle} onChange={(e) => setHideIdle(e.target.checked)} className="w-3.5 h-3.5 accent-[#4f46e5]" />
              Hide work centers with no activity
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className={th}>Cost Center / Work Center</th>
                  <th className={`${th} text-right`}>Labour</th>
                  <th className={`${th} text-right`}>Material</th>
                  <th className={`${th} text-right`}>Overhead</th>
                  <th className={`${th} text-right`}>Total Cost</th>
                  <th className={th}>Budget</th>
                  <th className={`${th} text-right`}>Run / Available</th>
                  <th className={`${th} text-right`}>Utilization</th>
                  <th className={`${th} text-right`}>Good / Rejected</th>
                  <th className={`${th} text-right`}>Downtime</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-[#94a3b8]">
                      <i className="ri-loader-4-line animate-spin text-xl block mb-1" />
                      Building report…
                    </td>
                  </tr>
                ) : visible.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-[#94a3b8]">
                      No Production, Quality or Maintenance cost centers yet.
                    </td>
                  </tr>
                ) : (
                  visible.map((cc) => {
                    const key = rowKey(cc);
                    const open = expanded.has(key);
                    return (
                      <Fragment key={key}>
                        <tr className="border-b border-[#f1f5f9] bg-white hover:bg-[#f8fafc] cursor-pointer" onClick={() => toggle(key)}>
                          <td className={td}>
                            <div className="flex items-center gap-2">
                              <i className={`ri-arrow-right-s-line text-[#94a3b8] transition-transform ${open ? 'rotate-90' : ''}`} />
                              <div>
                                <p className="font-semibold text-[#1e293b] text-sm">
                                  {cc.code && <span className="font-mono text-[11px] text-[#64748b] mr-1.5">{cc.code}</span>}
                                  {cc.name}
                                </p>
                                <p className="text-[11px] text-[#94a3b8]">
                                  {cc.id ? `${typeLabel(cc.type)}${cc.managerName ? ` · ${cc.managerName}` : ''} · ` : ''}
                                  {cc.workCenters.length} work center{cc.workCenters.length === 1 ? '' : 's'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className={`${td} text-right`}>{formatINR(cc.labourCost)}</td>
                          <td className={`${td} text-right`}>{formatINR(cc.materialCost)}</td>
                          <td className={`${td} text-right`}>{formatINR(cc.overheadCost)}</td>
                          <td className={`${td} text-right font-bold text-[#1e293b]`}>{formatINR(cc.totalCost)}</td>
                          <td className={td}>
                            {cc.budget > 0 && <p className="text-[11px] text-[#475569]">{formatINR(cc.budget)}</p>}
                            <BudgetBar used={cc.budgetUsedPct} />
                          </td>
                          <td className={`${td} text-right`}>{hrs(cc.actualHours)} / {hrs(cc.availableHours)}</td>
                          <td className={`${td} text-right font-semibold ${utilizationTone(cc.utilizationPct)}`}>{pct(cc.utilizationPct)}</td>
                          <td className={`${td} text-right`}>
                            {cc.goodQty.toLocaleString('en-IN')} / <span className={cc.rejectedQty > 0 ? 'text-rose-600' : ''}>{cc.rejectedQty.toLocaleString('en-IN')}</span>
                          </td>
                          <td className={`${td} text-right`}>{cc.downtimeHours > 0 ? hrs(cc.downtimeHours) : '—'}</td>
                        </tr>
                        {open &&
                          (cc.workCenters.length === 0 ? (
                            <tr className="bg-[#fcfcfd] border-b border-[#f1f5f9]">
                              <td colSpan={10} className="px-10 py-2.5 text-[#94a3b8]">
                                {hideIdle ? 'No activity in this period.' : 'No work centers are assigned to this cost center.'}
                              </td>
                            </tr>
                          ) : (
                            cc.workCenters.map((w) => (
                              <tr key={w.id ?? 'none'} className="bg-[#fcfcfd] border-b border-[#f1f5f9]">
                                <td className={`${td} pl-10`}>
                                  <p className="text-[#1e293b]">
                                    {w.code && <span className="font-mono text-[11px] text-[#64748b] mr-1.5">{w.code}</span>}
                                    {w.name}
                                    {!w.isActive && <span className="ml-1.5 text-[10px] text-[#94a3b8]">(inactive)</span>}
                                  </p>
                                  {w.plannedHours > 0 && <p className="text-[10px] text-[#94a3b8]">{hrs(w.plannedHours)} planned</p>}
                                </td>
                                <td className={`${td} text-right text-[#475569]`}>{formatINR(w.labourCost)}</td>
                                <td className={`${td} text-right text-[#475569]`}>{formatINR(w.materialCost)}</td>
                                <td className={`${td} text-right text-[#475569]`}>{formatINR(w.overheadCost)}</td>
                                <td className={`${td} text-right font-semibold text-[#1e293b]`}>{formatINR(w.totalCost)}</td>
                                <td className={td} />
                                <td className={`${td} text-right text-[#475569]`}>{hrs(w.actualHours)} / {hrs(w.availableHours)}</td>
                                <td className={`${td} text-right ${utilizationTone(w.utilizationPct)}`}>{pct(w.utilizationPct)}</td>
                                <td className={`${td} text-right text-[#475569]`}>
                                  {w.goodQty.toLocaleString('en-IN')} / <span className={w.rejectedQty > 0 ? 'text-rose-600' : ''}>{w.rejectedQty.toLocaleString('en-IN')}</span>
                                </td>
                                <td className={`${td} text-right text-[#475569]`}>{w.downtimeHours > 0 ? hrs(w.downtimeHours) : '—'}</td>
                              </tr>
                            ))
                          ))}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
              {totals && visible.length > 0 && costCenterFilter === 'ALL' && (
                <tfoot className="bg-[#f8fafc] border-t border-[#e2e8f0] font-semibold text-[#1e293b]">
                  <tr>
                    <td className={td}>Total</td>
                    <td className={`${td} text-right`}>{formatINR(totals.labourCost)}</td>
                    <td className={`${td} text-right`}>{formatINR(totals.materialCost)}</td>
                    <td className={`${td} text-right`}>{formatINR(totals.overheadCost)}</td>
                    <td className={`${td} text-right`}>{formatINR(totals.totalCost)}</td>
                    <td className={td}>{totals.budget > 0 ? formatINR(totals.budget) : '—'}</td>
                    <td className={`${td} text-right`}>{hrs(totals.actualHours)} / {hrs(totals.availableHours)}</td>
                    <td className={`${td} text-right ${utilizationTone(totals.utilizationPct)}`}>{pct(totals.utilizationPct)}</td>
                    <td className={`${td} text-right`}>{totals.goodQty.toLocaleString('en-IN')} / {totals.rejectedQty.toLocaleString('en-IN')}</td>
                    <td className={`${td} text-right`}>{totals.downtimeHours > 0 ? hrs(totals.downtimeHours) : '—'}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* How the figures are built */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 text-[11px] text-[#64748b] space-y-1">
          <p className="font-semibold text-[#475569] text-xs">How these figures are calculated</p>
          <p><strong>Labour</strong> — shop floor time × the operator's hourly wage (the work order's operator, else the average active operator), as in order costing.</p>
          <p><strong>Material</strong> — production issues in the period; a stage-mapped issue counts at that stage's work center, an order-level issue at the order's first stage.</p>
          <p><strong>Overhead</strong> — the Costing Setup overhead % on material + labour. <strong>Budget</strong> — the cost center's monthly budget, pro-rated by calendar day.</p>
          <p><strong>Available hours</strong> — working hours/day × efficiency % on the days of the work center's shifts (every day when no shift is set).</p>
        </div>
      </div>
    </AppLayout>
  );
}
